import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Filter, Ticket as TicketIcon, Bell } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { authenticatedFetch, getUserData } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { mapTicketListFromAPI } from "@/lib/translations";

interface Ticket {
  id: number;
  numero: string;
  titulo: string;
  descricao: string;
  categoria: string;
  prioridade: string;
  status: string;
  data_criacao: string;
  data_atualizacao: string;
  atribuido_para?: number;
  cliente_id?: number;
}

// Helper para determinar se o ticket precisa de ação do usuário
const needsUserAction = (ticket: Ticket, currentUser: any): boolean => {
  if (!currentUser) return false;
  
  const userType = currentUser.user_type;
  const userId = currentUser.id;
  const status = ticket.status;
  
  // Cliente precisa agir
  if (userType === 'client' && ticket.cliente_id === userId) {
    return status === 'Aguardando Cliente' || status === 'Aguardando Validação';
  }
  
  // Atendente precisa agir (ticket atribuído a ele)
  if (userType === 'internal' && ticket.atribuido_para === userId) {
    return ['Atribuído', 'Em Análise', 'Em Atendimento'].includes(status);
  }
  
  // Admin precisa agir (tickets novos sem atribuição)
  if (userType === 'internal' && currentUser.role === 'admin') {
    return status === 'Novo' && !ticket.atribuido_para;
  }
  
  return false;
};

const ClientTickets = () => {
  const navigate = useNavigate();
  const currentUser = getUserData();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  // Redirecionar usuários de suporte para página admin
  useEffect(() => {
    const user = getUserData();
    if (user?.user_type === 'internal' && user?.role_id === 4) {
      navigate('/cliente/admin/tickets', { replace: true });
      return;
    }
  }, [navigate]);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchTerm, statusFilter, categoryFilter]);

  const fetchTickets = async () => {
    try {
      setIsLoading(true);
      const response = await authenticatedFetch("/api/tickets/index.php");

      if (!response.ok) {
        throw new Error("Erro ao carregar tickets");
      }

      const data = await response.json();
      const mapped = mapTicketListFromAPI(data.data || []);
      setTickets(mapped);
    } catch (err) {
      console.error("Error fetching tickets:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = [...tickets];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (ticket) =>
          ticket.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ticket.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ticket.descricao.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((ticket) => ticket.status === statusFilter);
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter((ticket) => ticket.categoria === categoryFilter);
    }

    setFilteredTickets(filtered);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
      "Aberto": { variant: "destructive", label: "Aberto" },
      "Em Andamento": { variant: "default", label: "Em Andamento" },
      "Aguardando Cliente": { variant: "secondary", label: "Aguardando Cliente" },
      "Fechado": { variant: "outline", label: "Fechado" },
    };

    const config = variants[status] || { variant: "outline" as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (prioridade: string) => {
    const colors: Record<string, string> = {
      "Baixa": "bg-green-100 text-green-800 border-green-200",
      "Média": "bg-yellow-100 text-yellow-800 border-yellow-200",
      "Alta": "bg-orange-100 text-orange-800 border-orange-200",
      "Urgente": "bg-red-100 text-red-800 border-red-200",
    };

    return (
      <Badge variant="outline" className={cn("border", colors[prioridade] || "")}>
        {prioridade}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Data não disponível";
    try {
      // Converter formato "2026-01-19 22:52:27" para ISO "2026-01-19T22:52:27"
      const isoDate = dateString.replace(" ", "T");
      return new Date(isoDate).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (e) {
      return "Data inválida";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Meus Tickets</h1>
          <p className="text-muted-foreground mt-2">
            Gerencie suas solicitações de suporte
          </p>
        </div>
        <Button asChild>
          <Link to="/cliente/tickets/novo">
            <Plus className="mr-2 h-4 w-4" />
            Novo Ticket
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por número, título ou descrição..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="Aberto">Aberto</SelectItem>
                <SelectItem value="Em Andamento">Em Andamento</SelectItem>
                <SelectItem value="Aguardando Cliente">Aguardando Cliente</SelectItem>
                <SelectItem value="Fechado">Fechado</SelectItem>
              </SelectContent>
            </Select>

            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                <SelectItem value="Suporte Técnico">Suporte Técnico</SelectItem>
                <SelectItem value="Dúvida">Dúvida</SelectItem>
                <SelectItem value="Problema">Problema</SelectItem>
                <SelectItem value="Solicitação">Solicitação</SelectItem>
                <SelectItem value="Outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tickets List */}
      <div className="space-y-4">
        {isLoading ? (
          <>
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </>
        ) : filteredTickets.length > 0 ? (
          filteredTickets.map((ticket) => (
            <Card key={ticket.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <Link to={`/cliente/tickets/${ticket.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      {/* Header */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-sm font-medium text-muted-foreground">
                          #{ticket.numero}
                        </span>
                        {getStatusBadge(ticket.status)}
                        {getPriorityBadge(ticket.prioridade)}
                        <Badge variant="outline">{ticket.categoria}</Badge>
                        {needsUserAction(ticket, currentUser) && (
                          <Badge className="bg-orange-500 text-white animate-pulse">
                            <Bell className="h-3 w-3 mr-1" />
                            Aguardando sua ação
                          </Badge>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="text-lg font-semibold hover:text-primary transition-colors">
                        {ticket.titulo}
                      </h3>

                      {/* Description */}
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {ticket.descricao}
                      </p>

                      {/* Footer */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Criado em {formatDate(ticket.data_criacao)}</span>
                        <span>•</span>
                        <span>Atualizado em {formatDate(ticket.data_atualizacao)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <TicketIcon className="h-16 w-16 text-muted-foreground opacity-50 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhum ticket encontrado</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {searchTerm || statusFilter !== "all" || categoryFilter !== "all"
                  ? "Tente ajustar os filtros de busca"
                  : "Você ainda não possui tickets de suporte"}
              </p>
              {!searchTerm && statusFilter === "all" && categoryFilter === "all" && (
                <Button asChild>
                  <Link to="/cliente/tickets/novo">
                    <Plus className="mr-2 h-4 w-4" />
                    Criar Primeiro Ticket
                  </Link>
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ClientTickets;
